import React, { useState, useEffect } from 'react';
import { Printer, RefreshCw, CheckCircle, Info, ExternalLink, Copy, Image as ImageIcon, Upload, X, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { printerAPI, restaurantAPI, authAPI } from '../lib/api';
import { toast } from 'sonner';
import { useAutosave } from '../hooks/useAutosave';
import { AutosaveStatus } from '../components/AutosaveStatus';

export const PrinterSettingsPage = () => {
  const [config, setConfig] = useState({
    printer_api_key: '',
    printer_name: '',
    auto_print_enabled: true,
    print_copies: 1
  });
  const [receiptLogoUrl, setReceiptLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  // Deze pagina heeft geen velden waar een typfout geld kost: een printernaam,
  // een schakelaar en een aantal kopieen. Alles slaat zichzelf op.
  const autosave = useAutosave(
    async (velden) => { await printerAPI.saveConfig(velden); },
    {
      onOpgegeven: () => toast.error(
        'Je printerinstelling is niet opgeslagen. Controleer je '
        + 'internetverbinding en klik daarna op "Opnieuw proberen".',
        { duration: 15000 }),
    });

  // De backend verwacht deze drie samen, dus ze gaan samen mee. Het zijn er te
  // weinig om dat uit te splitsen.
  const zet = (wijziging) => {
    const nieuw = { ...config, ...wijziging };
    setConfig(nieuw);
    autosave.plan({
      printer_name: nieuw.printer_name,
      auto_print_enabled: nieuw.auto_print_enabled,
      print_copies: nieuw.print_copies,
    });
  };
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const printStationUrl = config.printer_api_key
    ? `${baseUrl}/print-station/${config.printer_api_key}`
    : '';

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const [cfgResp, profileResp] = await Promise.all([
        printerAPI.getConfig(),
        authAPI.getProfile().catch(() => ({ data: {} })),
      ]);
      setConfig(cfgResp.data);
      setReceiptLogoUrl(profileResp.data?.receipt_logo_url || '');
    } catch (e) {
      console.error('Error fetching printer config:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    try {
      const response = await printerAPI.generateKey();
      setConfig(prev => ({ ...prev, printer_api_key: response.data.printer_api_key }));
      toast.success('Nieuwe Print Station link gegenereerd!');
    } catch (e) {
      toast.error('Kon geen link genereren');
    } finally {
      setGeneratingKey(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(printStationUrl);
    setCopied(true);
    toast.success('URL gekopieerd!');
    setTimeout(() => setCopied(false), 2000);
  };


  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error('Logo te groot. Max 500 KB.');
      return;
    }
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        await restaurantAPI.updateProfile({ receipt_logo_url: dataUrl });
        setReceiptLogoUrl(dataUrl);
        toast.success('Bon-logo opgeslagen');
        setUploadingLogo(false);
      };
      reader.onerror = () => { toast.error('Kon logo niet lezen'); setUploadingLogo(false); };
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error('Upload mislukt');
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    try {
      await restaurantAPI.updateProfile({ receipt_logo_url: '' });
      setReceiptLogoUrl('');
      toast.success('Bon-logo verwijderd');
    } catch (e) {
      toast.error('Kon logo niet verwijderen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#1A4D2E] animate-spin" />
      </div>
    );
  }

  const qrUrl = printStationUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(printStationUrl)}`
    : '';

  return (
    <div className="space-y-6" data-testid="printer-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Printer Instellingen</h1>
        <p className="text-gray-500 mt-1">Print bonnen automatisch via de FoodFlows Print Station (browser-based)</p>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Printer className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-emerald-900 text-lg">Hoe werkt het?</h2>
            <p className="text-emerald-800 text-sm mt-1">
              Open de <strong>Print Station URL</strong> hieronder in Chrome op je tablet of PC. Zolang de pagina open
              staat (Chrome-tab actief), worden nieuwe bestellingen automatisch geprint naar je <strong>standaard
              printer</strong> — werkt met élke geïnstalleerde printer-app of de ingebouwde Android print service.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
              <div className="bg-white/70 rounded-lg p-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                <span className="text-emerald-900">Stel je printer als standaard in</span>
              </div>
              <div className="bg-white/70 rounded-lg p-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                <span className="text-emerald-900">Open Print Station URL in Chrome</span>
              </div>
              <div className="bg-white/70 rounded-lg p-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                <span className="text-emerald-900">Activeer Auto-Print</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Station URL */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
            <Printer className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Jouw Print Station</h2>
            <p className="text-gray-500 text-sm">Open deze unieke link op het apparaat waar je wilt printen</p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {config.printer_api_key ? (
            <>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Print Station URL</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 overflow-x-auto">
                    <code className="text-emerald-800 text-sm break-all font-mono" data-testid="print-station-url">
                      {printStationUrl}
                    </code>
                  </div>
                  <Button
                    onClick={copyUrl}
                    className={`${copied ? 'bg-emerald-600' : 'bg-emerald-500'} hover:bg-emerald-600 text-white px-4`}
                    data-testid="copy-print-station-url"
                  >
                    {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a href={printStationUrl} target="_blank" rel="noopener noreferrer" data-testid="open-print-station-link">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Print Station
                  </Button>
                </a>
                <Button
                  variant="ghost"
                  onClick={handleGenerateKey}
                  disabled={generatingKey}
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  data-testid="regenerate-key-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${generatingKey ? 'animate-spin' : ''}`} />
                  Nieuwe Link Genereren
                </Button>
              </div>

              {qrUrl && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl flex items-center gap-4">
                  <img src={qrUrl} alt="QR code Print Station" className="w-24 h-24 rounded-lg bg-white p-2" data-testid="print-station-qr" />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <QrCode className="w-4 h-4" /> Scan met je tablet
                    </h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Scan deze QR-code met je tablet camera om de Print Station direct te openen.
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Printer className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 mb-4">Nog geen Print Station link aangemaakt</p>
              <Button
                onClick={handleGenerateKey}
                disabled={generatingKey}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="generate-key-btn"
              >
                {generatingKey ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                Print Station Aanmaken
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Setup instructions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" /> Eerste setup (5 minuten)
        </h3>
        <ol className="text-gray-700 text-sm space-y-2 list-decimal ml-5">
          <li>Open op je tablet/PC: <strong>Instellingen → Connected Devices / Printers</strong> en stel je thermische printer in als <strong>standaard printer</strong>.</li>
          <li>Open Chrome en plak de Print Station URL hierboven (of scan de QR-code).</li>
          <li>Plaats een test-bestelling vanaf je telefoon op je webshop, of klik <strong>"Test Print"</strong> in de Print Station.</li>
          <li>Klik op de <strong>oranje "Print Volgende"</strong> knop wanneer de bon binnenkomt.</li>
          <li>In de print-dialoog: kies je printer en vink <strong>"Altijd gebruiken / Save as default"</strong> aan.</li>
          <li>Klik op <strong>"Activeer Auto-Print"</strong> in de Print Station — vanaf nu print élke nieuwe bestelling volledig automatisch.</li>
        </ol>
        <div className="mt-3 p-3 bg-blue-50 rounded-lg flex gap-2">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-blue-800 text-xs">
            <strong>Tip:</strong> Houd de Chrome-tab van Print Station altijd open. Gebruik op Android een <em>kiosk browser</em> app (bv. "Fully Kiosk Browser") om de tablet ingelogd te houden.
          </p>
        </div>
      </div>

      {/* Print Options */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
          <div className="w-12 h-12 bg-[#1A4D2E]/10 rounded-xl flex items-center justify-center">
            <Printer className="w-6 h-6 text-[#1A4D2E]" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Print Opties</h2>
            <p className="text-gray-500 text-sm">Configureer hoe bonnen geprint worden</p>
          </div>
        </div>

        <div className="grid gap-6">
          <div className="space-y-2">
            <Label htmlFor="printer_name">Printer Naam (label)</Label>
            <Input
              id="printer_name"
              type="text"
              value={config.printer_name || ''}
              onChange={(e) => zet({ printer_name: e.target.value })}
              placeholder="bijv. Keuken Printer"
              data-testid="printer-name-input"
            />
          </div>

          {/* Receipt logo upload */}
          <div className="space-y-2 p-4 bg-gray-50 rounded-xl">
            <Label className="text-gray-900 font-medium flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Logo op kassabon
            </Label>
            <p className="text-gray-500 text-sm mb-3">
              Verschijnt bovenaan elke gedrukte bon. Max 500 KB. Een monochrome PNG werkt het best.
            </p>
            {receiptLogoUrl ? (
              <div className="flex items-start gap-4">
                <img
                  src={receiptLogoUrl}
                  alt="Bon logo"
                  className="w-24 h-24 object-contain bg-white border border-gray-200 rounded-lg p-2"
                  data-testid="receipt-logo-preview"
                />
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 text-sm px-3 py-2 rounded-lg cursor-pointer" data-testid="receipt-logo-change">
                    <Upload className="w-4 h-4" /> Vervangen
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files?.[0])} disabled={uploadingLogo} />
                  </label>
                  <button
                    type="button"
                    onClick={handleLogoRemove}
                    className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm"
                    data-testid="receipt-logo-remove"
                  >
                    <X className="w-4 h-4" /> Verwijderen
                  </button>
                </div>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm px-4 py-2 rounded-lg cursor-pointer" data-testid="receipt-logo-upload">
                {uploadingLogo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploadingLogo ? 'Uploaden...' : 'Logo Uploaden'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files?.[0])} disabled={uploadingLogo} />
              </label>
            )}
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div>
              <Label className="text-gray-900 font-medium">Auto-print bij acceptatie</Label>
              <p className="text-gray-500 text-sm mt-1">
                Print automatisch zodra je een bestelling accepteert
              </p>
            </div>
            <Switch
              checked={config.auto_print_enabled}
              onCheckedChange={(checked) => zet({ auto_print_enabled: checked })}
              data-testid="auto-print-toggle"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="print_copies">Aantal kopieën per bestelling</Label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => zet({ print_copies: Math.max(1, config.print_copies - 1) })}
                data-testid="copies-minus"
              >
                -
              </Button>
              <span className="w-12 text-center font-medium text-lg" data-testid="copies-value">{config.print_copies}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => zet({ print_copies: Math.min(5, config.print_copies + 1) })}
                data-testid="copies-plus"
              >
                +
              </Button>
            </div>
          </div>
        </div>

        {/* Geen knop meer; wel altijd zichtbaar wat er met je wijziging
            gebeurde, want anders voelt automatisch opslaan onbetrouwbaar. */}
        <div className="pt-4 border-t border-gray-100 min-h-[2rem]">
          {autosave.status === 'rust' ? (
            <p className="text-sm text-gray-500">
              Wijzigingen worden vanzelf opgeslagen.
            </p>
          ) : (
            <AutosaveStatus
              status={autosave.status}
              opgeslagenOp={autosave.opgeslagenOp}
              foutmelding={autosave.foutmelding}
              onOpnieuw={autosave.probeerOpnieuw}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PrinterSettingsPage;
